import { ShopCover } from "@/components/hub/ShopCover";
import { FeaturedDiscounts } from "@/components/hub/FeaturedDiscounts";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState, useEffect, useMemo } from "react";
import { useGetHomeData, useGetOrderStores, getGetOrderStoresQueryKey, useGetMyOrders, getGetMyOrdersQueryKey } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "wouter";
import { useTown } from "@/hooks/use-town";
import { useSEO } from "@/hooks/use-seo";
import { useAuth } from "@/hooks/use-auth";
import { usePendingOrders } from "@/context/PendingOrdersContext";
import { useAnalytics } from "@/hooks/use-analytics";
import { SponsorBanner } from "@/components/SponsorBanner";
import type { HomeContext, HomeSection } from "@workspace/api-client-react";
import { ExploreGrid } from "@/components/home/ExploreGrid";
import {
  SectionShell, FoodCategoryRail, VenueFeatureRail, TradesSection,
  GoingOutSection, type TonightEntry,
} from "@/components/home/HomeSections";
import {
  ShoppingBag, Tag, Calendar, Package,
  ChevronLeft, ChevronRight, MapPin, Truck, Store,
  Search, Star, ArrowRight, LifeBuoy, RefreshCw, Sparkles,
} from "lucide-react";

type HomeSectionSpec = HomeSection;

/** One row of `GET /whats-on`, narrowed to what this page renders. */
interface WhatsOnHomeItem {
  id: string;
  title: string;
  category: string;
  time: string | null;
  bar: { id: number; name: string; slug: string | null; town: string | null };
}

const WHATS_ON_LABEL: Record<string, string> = {
  quiz_night: "Quiz Night", karaoke: "Karaoke", live_music: "Live Music",
  sport: "Sport", darts: "Darts", pool: "Pool", open_mic: "Open Mic",
  comedy: "Comedy", dj: "DJ Night", theme_night: "Theme Night",
  happy_hour: "Happy Hour", other: "Event",
};

/**
 * Hero art per day part — the same photos the app uses, keyed off the same
 * server-decided context so the two platforms show the same thing at the same
 * hour. Web was a flat navy gradient, which read as plain next to the app.
 * Afternoon and late borrow the neighbouring shot because that is what is
 * actually open then.
 */
import { formatOfferWithMin } from "@/components/hub/store-utils";

const HERO_IMAGES: Record<NonNullable<HomeContext["dayPart"]>, string> = {
  breakfast: "/tiles/food-breakfast.webp",
  lunch:     "/tiles/food-lunch.webp",
  afternoon: "/tiles/food-lunch.webp",
  dinner:    "/tiles/food-dinner.webp",
  late:      "/tiles/food-dinner.webp",
};

/** Search hints what people are likely looking for at this hour. */
const SEARCH_PLACEHOLDER: Record<NonNullable<HomeContext["dayPart"]>, string> = {
  breakfast: "Search breakfast, cafes, shops…",
  lunch:     "Search lunch, bakeries, shops…",
  afternoon: "Search food, shops, trades…",
  dinner:    "Search takeaways, pubs, events…",
  late:      "Search what's open now…",
};

/**
 * Shown for the moment before `GET /home` returns, and if it ever fails.
 * Ordering only — never guess at what's on, because guessing is exactly what
 * the context engine exists to avoid.
 */
const DEFAULT_SECTIONS: HomeSectionSpec[] = [
  { id: "open_now", title: "Open now",    priority: 10, limit: 12 },
  { id: "shops",    title: "Local shops", priority: 35, limit: 8 },
  { id: "explore",  title: "Explore Ayrshire Hub", priority: 99, limit: 0 },
];

// ─── Scroll row with nav arrows ───────────────────────────────────────────────

function ScrollRow({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = () => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    el.addEventListener("scroll", update, { passive: true });
    return () => { el.removeEventListener("scroll", update); ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scroll = (dir: 1 | -1) => ref.current?.scrollBy({ left: dir * 300, behavior: "smooth" });

  return (
    <div className="relative group/scroll">
      {canLeft && (
        <button
          onClick={() => scroll(-1)}
          className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-white shadow-lg border border-slate-100 rounded-full flex items-center justify-center text-slate-600 hover:text-primary transition-all"
          aria-label="Scroll left"
        >
          <ChevronLeft size={16} />
        </button>
      )}
      <div ref={ref} className="flex items-start gap-4 overflow-x-auto pb-3 snap-x scrollbar-none">
        {children}
      </div>
      {canRight && (
        <button
          onClick={() => scroll(1)}
          className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-white shadow-lg border border-slate-100 rounded-full flex items-center justify-center text-slate-600 hover:text-primary transition-all"
          aria-label="Scroll right"
        >
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  );
}

// ─── Section header ────────────────────────────────────────────────────────────

function SectionHeader({
  icon, title, sub, href, hrefLabel = "See all",
}: {
  icon: React.ReactNode; title: string; sub?: string; href?: string; hrefLabel?: string;
}) {
  return (
    <div className="flex items-end justify-between mb-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-primary/8 flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div>
          <h2 className="font-bold text-[17px] text-foreground leading-tight">{title}</h2>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
      </div>
      {href && (
        <Link href={href} className="flex items-center gap-1 text-sm font-semibold text-primary hover:opacity-75 transition-opacity ml-4 flex-shrink-0">
          {hrefLabel} <ArrowRight size={13} />
        </Link>
      )}
    </div>
  );
}

// ─── Offer banner ─────────────────────────────────────────────────────────────

function OfferBanner({
  offers, deals,
}: {
  offers?: Array<{ type: string; value: number; minOrderPence: number; title?: string; scoped?: boolean }> | null;
  deals?: Array<{ name: string; qty: number; pricePence: number }> | null;
}) {
  const deal = deals?.[0];
  const offer = offers?.[0];
  if (!deal && !offer) return null;
  const label = deal
    ? `Any ${deal.qty} for £${(deal.pricePence / 100).toFixed(2)}`
    : (offer?.scoped && offer.title) ? offer.title
    : offer?.type === "percentage" ? `${offer.value}% off`
    : offer?.type === "fixed" ? `£${(offer.value / 100).toFixed(2)} off`
    : offer?.type === "free_delivery" ? "Free delivery"
    : offer?.type === "free_item" ? "Free item"
    : "Special offer";
  return (
    <div className="px-2.5 py-1.5 flex items-center gap-1.5 rounded-full bg-[#fff8e6] border border-amber-200 shadow-sm">
      <span className="text-[#1e3a5f] text-[9px] font-bold tracking-wide uppercase">Deal</span>
      <span className="text-[#1e3a5f] text-[11px] font-semibold truncate">{label}{offer && offer.minOrderPence > 0 ? ` · min £${(offer.minOrderPence / 100).toFixed(2)}` : ""}</span>
    </div>
  );
}

// ─── Store card ───────────────────────────────────────────────────────────────

type StoreMin = {
  id: number; slug?: string | null; name: string; category: string; town: string;
  coverMode?: string; coverAnimationUrl?: string | null; coverPosterUrl?: string | null; coverVideoUrl?: string | null;
  imageUrl?: string | null; bannerUrl?: string | null; offersDelivery: boolean; offersCollection: boolean;
  deliveryFeePence: number; deliveryTimeMins: number; isOpen?: boolean | null;
  acceptingOrders?: boolean | null; nextOpenTime?: string | null; hasDeliveryZones?: boolean;
  activeOffers?: Array<{ type: string; value: number; minOrderPence: number; title?: string; scoped?: boolean }> | null;
  mixMatchDeals?: Array<{ name: string; qty: number; pricePence: number }> | null;
  avgRating?: number; reviewCount?: number; multiStoreEligible?: boolean;
  isBoosted?: boolean; sponsored?: boolean | null; sponsoredUntil?: string | null;
};

function StoreCard({ store, prefix = "/order" }: { store: StoreMin; prefix?: string }) {
  const photoUrl = store.bannerUrl || store.coverPosterUrl;
  const open = store.isOpen !== false && store.acceptingOrders !== false;
  const href = `${prefix}/${store.slug ?? store.id}`;
  const sponsored = store.isBoosted || (store.sponsored && (!store.sponsoredUntil || new Date(store.sponsoredUntil) > new Date()));
  const freeDelivery = store.offersDelivery && store.deliveryFeePence === 0 && !store.hasDeliveryZones;
  return (
    <Link href={href} className={`group flex-shrink-0 w-[220px] self-start block snap-start rounded-[26px] ${sponsored ? "boosted-card" : ""} ${!open && !sponsored ? "opacity-60" : ""}`}>
      <div className="bg-white rounded-[24px] border border-slate-200/80 overflow-hidden shadow-[0_4px_20px_#10233d08] transition-shadow hover:shadow-md">
      {photoUrl && <div className="relative h-[144px] bg-slate-50 overflow-hidden">
        {photoUrl ? (
          <ShopCover store={store} alt={`${store.name} banner`} className="w-full h-full group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/6 to-primary/3 flex items-center justify-center">
            <ShoppingBag size={32} className="text-primary/20" />
          </div>
        )}
        {!open && (
          <div className="absolute inset-0 bg-black/35 flex items-end p-2.5">
            <span className="text-[10px] font-bold text-white bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full">
              {store.acceptingOrders === false ? "Temporarily closed" : "Closed"}
            </span>
          </div>
        )}
        {freeDelivery && open && (
          <span className="absolute bottom-3 right-3 text-[9px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Free delivery</span>
        )}
        {store.imageUrl && <img src={store.imageUrl} alt={`${store.name} logo`} className="absolute bottom-3 left-3 w-14 h-14 rounded-xl border-2 border-white bg-white shadow-md object-contain" loading="lazy"/>}
      </div>}
      <div className="p-4">
        {!(store.bannerUrl || store.coverPosterUrl) && store.imageUrl && <img src={store.imageUrl} alt={`${store.name} logo`} className="float-right ml-3 mb-2 w-14 h-14 rounded-xl border border-slate-200 bg-white object-contain" loading="lazy"/>}
        {sponsored && <p className="text-[10px] font-semibold text-amber-700 mb-1.5">Sponsored</p>}
        {!photoUrl && !open && <p className="text-xs text-slate-500 mb-1.5">{store.acceptingOrders === false ? "Temporarily closed" : "Closed"}</p>}
        <p className="font-bold text-[17px] tracking-tight text-[#1e3a5f] line-clamp-1 group-hover:text-primary transition-colors">{store.name}</p>
        <p className="text-[11px] text-slate-400 mt-0.5 truncate">{store.category} · {store.town}</p>
        <div className="mt-2 empty:hidden flex"><OfferBanner offers={store.activeOffers} deals={store.mixMatchDeals} /></div>
        <div className="flex items-center gap-2.5 mt-3 pt-3 border-t border-slate-100 flex-wrap">
          {open && store.offersDelivery && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-600">
              <Truck size={9} /> {store.deliveryTimeMins}m
            </span>
          )}
          {open && store.offersCollection && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-600">Collect</span>
          )}
          {(store.avgRating ?? 0) > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] font-bold text-amber-600">
              <Star size={9} fill="currentColor" /> {store.avgRating?.toFixed(1)}
            </span>
          )}
        </div>
      </div>
      </div>
    </Link>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────

function SkeletonStoreCard() {
  return (
    <div className="flex-shrink-0 w-[220px] bg-white rounded-2xl border border-slate-100/80 shadow-sm overflow-hidden snap-start">
      <div className="h-[132px] skeleton-shimmer" />
      <div className="p-3.5 pt-3 space-y-2">
        <div className="h-4 skeleton-shimmer rounded w-3/4" />
        <div className="h-3 skeleton-shimmer rounded w-1/2" />
        <div className="flex gap-1.5 mt-2">
          <div className="h-4 skeleton-shimmer rounded-full w-14" />
          <div className="h-4 skeleton-shimmer rounded-full w-12" />
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
function quoteTimeRemaining(createdAt: string) {
  const remaining = new Date(createdAt).getTime() + SEVEN_DAYS - Date.now();
  if (remaining <= 0) return { label: "Expired", colour: "text-slate-400" };
  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  const label = days > 0 ? `${days}d ${hours}h left` : hours > 0 ? `${hours}h ${mins}m left` : `${mins}m left`;
  const colour = days >= 3 ? "text-emerald-600" : days >= 1 ? "text-amber-600" : "text-red-500";
  return { label, colour };
}

const URGENCY_COLOUR: Record<string, string> = {
  emergency: "bg-red-50 text-red-700 border border-red-100",
  within_24h: "bg-orange-50 text-orange-700 border border-orange-100",
  this_week: "bg-amber-50 text-amber-700 border border-amber-100",
  this_month: "bg-yellow-50 text-yellow-700 border border-yellow-100",
  flexible: "bg-green-50 text-green-700 border border-green-100",
};

function isStoreOpen(s: { isOpen?: boolean | null; acceptingOrders?: boolean | null }) {
  return s.isOpen !== false && s.acceptingOrders !== false;
}
function sortOpen<T extends { isOpen?: boolean | null; acceptingOrders?: boolean | null }>(arr: T[]): T[] {
  return [...arr].sort((a, b) => (isStoreOpen(a) ? 0 : 1) - (isStoreOpen(b) ? 0 : 1));
}

// ─── Time-aware greeting ───────────────────────────────────────────────────────

// ─── Active order banner ───────────────────────────────────────────────────────

const ACTIVE_STATUSES = new Set(["pending", "confirmed", "preparing", "ready", "out_for_delivery"]);
const STATUS_LABELS: Record<string, string> = {
  pending: "Order received", confirmed: "Confirmed", preparing: "Being prepared",
  ready: "Ready to collect", out_for_delivery: "On the way",
};

function ActiveOrderBanner() {
  const { user } = useAuth();
  // Was polling `/api/orders/my-orders` every 30s — a route that does not
  // exist, so this banner had never once rendered and the poll 404'd forever.
  // Now on the real hook, sharing the query key (and therefore the single
  // poller) with the site-wide OrderTrackerDock.
  const { data } = useGetMyOrders({
    query: {
      queryKey: getGetMyOrdersQueryKey(),
      enabled: !!user,
      staleTime: 15000,
      refetchInterval: (query) => {
        const hasActive = (query.state.data as { items?: { status: string }[] } | undefined)
          ?.items?.some(o => ACTIVE_STATUSES.has(o.status));
        return hasActive ? 30000 : false;
      },
    },
  });
  const active = data?.items?.find(o => ACTIVE_STATUSES.has(o.status));
  if (!active) return null;
  const activeEx = active as unknown as { driverMode?: string | null; deliveryCode?: string | null };
  // The driver cannot finish the delivery without this code, so it travels
  // with the status wherever the status is shown.
  const code = activeEx.driverMode === "hub" && active.fulfilmentType === "delivery"
    ? activeEx.deliveryCode || null
    : null;
  return (
    <section className="mt-4 px-4 max-w-screen-xl mx-auto">
      <Link href={`/order/track/${active.id}`}>
        <div className="rounded-2xl bg-primary px-4 py-3.5 flex items-center gap-3 hover:opacity-95 transition-opacity cursor-pointer shadow-md shadow-primary/20">
          <div className="w-8 h-8 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <Package size={16} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-[13px] font-bold">{STATUS_LABELS[active.status] ?? active.status}</p>
            <p className="text-white/65 text-[11px] truncate mt-0.5">{active.store?.name ?? "Your order"}</p>
          </div>
          {code && (
            <span className="flex-shrink-0 flex flex-col items-center rounded-lg bg-white/15 px-2.5 py-1">
              <span className="text-white/60 text-[9px] font-bold tracking-widest uppercase leading-none">Code</span>
              <span className="text-[#f0a500] text-base font-bold tracking-[0.2em] ml-[0.2em] leading-tight">{code}</span>
            </span>
          )}
          <span className="text-white/80 text-xs font-semibold flex items-center gap-1">Track <ArrowRight size={13} /></span>
        </div>
      </Link>
    </section>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────


export default function Home() {
  const { user } = useAuth();
  const { hasStore } = usePendingOrders();
  const { track } = useAnalytics();
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const { townParam, selectedTown } = useTown();

  const { data: homeData, isLoading } = useGetHomeData({ town: townParam });

  // The server decides what this hour looks like — see api-server
  // lib/homeContext.ts. Doing it here instead would let a device with a wrong
  // clock show breakfast at teatime, and would let web and mobile disagree.
  // Until the payload arrives we render the ordering essentials in their
  // default order rather than an empty page.
  const ctx = homeData?.context;

  useEffect(() => { track("page_viewed", { screen: "home" }); }, []);
  useEffect(() => {
    if (ctx) track("homepage_context", { screen: "home", town: selectedTown, metadata: { dayPart: ctx.dayPart, dayType: ctx.dayType } });
  }, [ctx?.dayPart, ctx?.dayType, selectedTown]);

  useSEO({
    title: "Ayrshire Hub — Order Food, Shop Local & Discover Ayrshire",
    description: "Order from Ayrshire restaurants & shops, find events, deals, jobs, trades and local news. Your community super-app.",
  });

  const { data: whatsOnData } = useQuery<{ items: WhatsOnHomeItem[] }>({
    queryKey: ["whats-on-home", townParam],
    // venueOnly=true: this strip is explicitly about pubs, so it keeps the
    // pre-phase-4 set (items attached to a real venue). Venue-less events — a
    // gala in a park, a market — belong on /whats-on and have no venue page.
    queryFn: () => fetch(`/api/whats-on?days=7${townParam ? `&town=${encodeURIComponent(townParam)}` : ""}&limit=8&venueOnly=true`).then(r => r.json()) as Promise<{ items: WhatsOnHomeItem[] }>,
    staleTime: 60000,
  });

  const locationParams = townParam ? { town: townParam } : {};
  const { data: foodStoresData, isLoading: foodLoading } = useGetOrderStores(
    { ...locationParams, type: "food" as const },
    { query: { queryKey: getGetOrderStoresQueryKey({ ...locationParams, type: "food" as const }), staleTime: 60000 } }
  );
  const { data: retailStoresData, isLoading: retailLoading } = useGetOrderStores(
    { ...locationParams, type: "retail" as const },
    { query: { queryKey: getGetOrderStoresQueryKey({ ...locationParams, type: "retail" as const }), staleTime: 60000 } }
  );

  const allFood = useMemo(() => sortOpen(foodStoresData?.items ?? []), [foodStoresData]);
  const foodStores = useMemo(() => allFood.slice(0, 10), [allFood]);
  const retailStores = useMemo(() => sortOpen(retailStoresData?.items ?? []).slice(0, 10), [retailStoresData]);

  /** Stores taking orders this minute. `isOpen` is the server's answer from the
   *  same opening-hours engine checkout uses, so this can never disagree with
   *  whether an order will actually be accepted. */
  const openFood = useMemo(
    () => allFood.filter(s => s.isOpen !== false
      && (s as { acceptingOrders?: boolean | null }).acceptingOrders !== false),
    [allFood],
  );

  /** Only promote a category if a store in it exists here — otherwise the chip
   *  leads to an empty results page. */
  const availableCategories = useMemo(
    () => new Set(allFood.map(s => s.category).filter(Boolean) as string[]),
    [allFood],
  );
  const venueFacilitySlugs = useMemo(
    () => new Set(homeData?.venueFacilitySlugs ?? []),
    [homeData?.venueFacilitySlugs],
  );

  const { data: basketData } = useQuery<{ basket: { storeId: number; storeName: string | null; estimatedSubtotalPence: number | null } | null }>({
    queryKey: ["my-basket-recovery"],
    queryFn: () => fetch("/api/order/my-baskets", { credentials: "include" }).then(r => r.json()) as Promise<{ basket: { storeId: number; storeName: string | null; estimatedSubtotalPence: number | null } | null }>,
    enabled: !!user,
    staleTime: 60000,
  });
  const abandonedBasket = basketData?.basket ?? null;

  type HomeDeal = { offerId: number; title: string; type: string; value: number; minOrderPence: number; bogofBuyQty?: number; bogofFreeQty?: number; scoped?: boolean; image: string | null; storeId: number; storeName: string; storeSlug: string | null };
  const { data: dealsData, isLoading: dealsLoading } = useQuery<{ deals: HomeDeal[] }>({
    queryKey: ["homepage-deals"],
    queryFn: () => fetch("/api/order/homepage-deals").then(r => r.json()) as Promise<{ deals: HomeDeal[] }>,
    staleTime: 60_000,
  });
  const homepageDeals = dealsData?.deals ?? [];

  const heroImage = ctx ? HERO_IMAGES[ctx.dayPart] : null;
  const firstName = user?.name?.split(" ")[0];
  const greeting = ctx ? `${ctx.greeting}${firstName ? `, ${firstName}` : ""}` : "";
  const townLabel = selectedTown !== "All Towns" ? selectedTown : "Ayrshire";

  const hasAnyContent = foodStores.length > 0 || retailStores.length > 0 ||
    (homeData?.latestDeals ?? []).length > 0 || (homeData?.upcomingEvents ?? []).length > 0 ||
    (homeData?.latestJobs ?? []).length > 0 || (homeData?.latestNews ?? []).length > 0;
  const showNothingHere = !isLoading && !foodLoading && !retailLoading && !hasAnyContent && selectedTown !== "All Towns";

  /** Tonight's pub listings, for the "going out" section. */
  const tonight = useMemo<TonightEntry[]>(() => {
    const items = whatsOnData?.items ?? [];
    return items.slice(0, 6).map(i => ({
      id: i.id,
      title: i.title,
      category: WHATS_ON_LABEL[i.category] ?? null,
      time: i.time,
      venueName: i.bar?.name ?? null,
      town: i.bar?.town ?? null,
      href: i.bar?.slug ? `/venue/${i.bar.slug}` : `/venues/${i.bar?.id ?? ""}`,
    }));
  }, [whatsOnData]);

  const sectionEvent = (id: string) => () => track("section_clicked", { screen: "home", metadata: { section: id } });

  /**
   * Render one planned section, or nothing.
   *
   * Returning null is the empty-section rule in force: the server proposes a
   * section, and if there is no real content behind it the homepage simply does
   * not mention it. Every destination that can disappear this way is still
   * listed in the Explore grid at the foot of the page.
   */
  function renderSection(spec: HomeSectionSpec): React.ReactNode {
    switch (spec.id) {
      // Rendered inside the hero, not as a standalone band.
      case "order_hero":
      case "food_categories":
        return null;

      case "open_now": {
        if (foodLoading) {
          return (
            <SectionShell key={spec.id} title={spec.title} href="/order">
              <ScrollRow>{Array.from({ length: 5 }).map((_, i) => <SkeletonStoreCard key={i} />)}</ScrollRow>
            </SectionShell>
          );
        }
        // Prefer stores actually taking orders; if nothing is open (the small
        // hours) fall back to the full list rather than an empty band, retitled
        // so it never claims a closed shop is open.
        // Featured discounts do not remove shops from the normal food list.
        // Open stores first, then backfill with more nearby stores.
        const openList = openFood;
        const seenIds = new Set(openList.map((s) => s.id));
        const filler = foodStores.filter((s) => !seenIds.has(s.id));
        const list = [...openList, ...filler].slice(0, spec.limit);
        if (list.length === 0) return null;
        const title = openList.length > 0 ? spec.title : "Food nearby";
        const subtitle = openList.length > 0
          ? `${openList.length} taking orders in ${townLabel}`
          : "Nothing open right now — here's what's nearby";
        return (
          <SectionShell key={spec.id} title={title} subtitle={subtitle} href="/order" onSeeAll={sectionEvent(spec.id)}>
            <ScrollRow>{list.map(s => <StoreCard key={s.id} store={s} prefix="/order" />)}</ScrollRow>
          </SectionShell>
        );
      }

      case "offers": {
        const deals = (homeData?.latestDeals ?? []).slice(0, spec.limit);
        if (isLoading || deals.length === 0) return null;
        return (
          <SectionShell key={spec.id} title={spec.title} subtitle="Discounts from Ayrshire businesses" href="/deals" onSeeAll={sectionEvent(spec.id)}>
            <ScrollRow>
              {deals.map(deal => (
                <Link key={deal.id} href={`/deals/${deal.slug ?? deal.id}`} className="group flex-shrink-0 w-[200px] bg-white rounded-2xl border border-slate-100/80 shadow-sm overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg block snap-start">
                  {deal.imageUrl ? (
                    <div className="h-[108px] overflow-hidden"><img src={deal.imageUrl} alt={deal.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" /></div>
                  ) : (
                    <div className="h-[108px] bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center"><Tag size={26} className="text-emerald-200" /></div>
                  )}
                  <div className="p-3">
                    <p className="font-bold text-[13px] text-slate-800 line-clamp-2 leading-snug group-hover:text-primary transition-colors">{deal.title}</p>
                    <p className="text-[11px] text-slate-400 mt-1 truncate">{deal.businessName}</p>
                    <span className="inline-block mt-1.5 text-[10px] font-bold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-100">{deal.town}</span>
                  </div>
                </Link>
              ))}
            </ScrollRow>
          </SectionShell>
        );
      }

      case "going_out_tonight": {
        // Renders venues as a fallback when nothing is listed, so this only
        // disappears entirely when there is neither an event nor a pub feature.
        const body = (
          <GoingOutSection
            entries={tonight}
            fallbackFeatureSlugs={venueFacilitySlugs}
            town={selectedTown}
            onEntry={() => track("event_clicked", { screen: "home" })}
            onFeature={(slug) => track("venue_feature_filter_selected", { screen: "home", metadata: { slug } })}
          />
        );
        if (!body || (tonight.length === 0 && venueFacilitySlugs.size === 0)) return null;
        return (
          <SectionShell key={spec.id} title={spec.title} subtitle={spec.subtitle} href="/whats-on" onSeeAll={sectionEvent(spec.id)}>
            {body}
          </SectionShell>
        );
      }

      case "whats_on_today":
      case "whats_on_weekend": {
        const events = (homeData?.upcomingEvents ?? []).slice(0, spec.limit);
        if (isLoading || events.length === 0) return null;
        return (
          <SectionShell key={spec.id} title={spec.title} href="/events" onSeeAll={sectionEvent(spec.id)}>
            <ScrollRow>
              {events.map(item => {
                const d = new Date(item.eventDate);
                return (
                  <Link key={item.id} href={`/events/${item.slug ?? item.id}`} className="group flex-shrink-0 w-[195px] bg-white rounded-2xl border border-slate-100/80 shadow-sm overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg block snap-start">
                    {item.imageUrl ? (
                      <div className="h-[108px] overflow-hidden"><img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" /></div>
                    ) : (
                      <div className="h-[108px] bg-gradient-to-br from-violet-50 to-indigo-50 flex items-center justify-center"><Calendar size={26} className="text-violet-200" /></div>
                    )}
                    <div className="p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-violet-600">
                        {isNaN(d.getTime()) ? item.eventDate : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                      </p>
                      <p className="font-bold text-[13px] text-slate-800 line-clamp-2 leading-snug mt-0.5 group-hover:text-primary transition-colors">{item.name}</p>
                      {item.venue && <p className="text-[11px] text-slate-400 mt-1 truncate">{item.venue}</p>}
                    </div>
                  </Link>
                );
              })}
            </ScrollRow>
          </SectionShell>
        );
      }

      case "venue_features": {
        const rail = <VenueFeatureRail availableSlugs={venueFacilitySlugs} town={selectedTown} onPick={(slug) => track("venue_feature_filter_selected", { screen: "home", metadata: { slug } })} />;
        if (venueFacilitySlugs.size === 0) return null;
        return (
          <SectionShell key={spec.id} title={spec.title} subtitle={spec.subtitle} href="/places" onSeeAll={sectionEvent(spec.id)}>
            {rail}
          </SectionShell>
        );
      }

      case "trades":
        return (
          <SectionShell key={spec.id} title={spec.title} subtitle={spec.subtitle} href="/jobs" seeAllLabel="See requests" onSeeAll={sectionEvent(spec.id)}>
            <TradesSection onPick={(label) => track("trade_clicked", { screen: "home", metadata: { trade: label } })} />
          </SectionShell>
        );

      case "shops": {
        if (retailLoading) {
          return (
            <SectionShell key={spec.id} title={spec.title} href="/shop-local">
              <ScrollRow>{Array.from({ length: 5 }).map((_, i) => <SkeletonStoreCard key={i} />)}</ScrollRow>
            </SectionShell>
          );
        }
        if (retailStores.length === 0) return null;
        return (
          <SectionShell key={spec.id} title={spec.title} subtitle="Independent Ayrshire shops & groceries" href="/shop-local" onSeeAll={sectionEvent(spec.id)}>
            <ScrollRow>{retailStores.map(s => <StoreCard key={s.id} store={s} prefix="/order" />)}</ScrollRow>
          </SectionShell>
        );
      }

      case "news": {
        const news = (homeData?.latestNews ?? []).slice(0, spec.limit);
        if (isLoading || news.length === 0) return null;
        return (
          <SectionShell key={spec.id} title={spec.title} subtitle="Latest stories across Ayrshire" href="/news" onSeeAll={sectionEvent(spec.id)}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {news.map(n => (
                <Link key={n.id} href={`/news/${n.slug ?? n.id}`} className="group bg-white rounded-2xl border border-slate-100/80 shadow-sm overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg block">
                  {n.imageUrl && <div className="h-[130px] overflow-hidden"><img src={n.imageUrl} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" /></div>}
                  <div className="p-3.5">
                    {n.breaking && <span className="inline-block mb-1.5 text-[9px] font-black uppercase tracking-widest bg-red-500 text-white px-2 py-0.5 rounded">Breaking</span>}
                    <p className="font-bold text-[14px] text-slate-800 line-clamp-2 leading-snug group-hover:text-primary transition-colors">{n.title}</p>
                    <p className="text-[11px] text-slate-400 mt-1.5">{n.town}</p>
                  </div>
                </Link>
              ))}
            </div>
          </SectionShell>
        );
      }

      case "jobs": {
        // `latestJobs` is quote requests from residents, not vacancies.
        const jobs = (homeData?.latestJobs ?? []).slice(0, spec.limit);
        if (isLoading || jobs.length === 0) return null;
        return (
          <SectionShell key={spec.id} title="Quote requests" subtitle="Residents looking for tradespeople" href="/jobs" onSeeAll={sectionEvent(spec.id)}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {jobs.map(job => (
                <Link key={job.id} href={`/jobs/${job.id}`} className="group bg-white rounded-2xl border border-slate-100/80 shadow-sm p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg block">
                  <p className="font-bold text-[14px] text-slate-800 line-clamp-2 group-hover:text-primary transition-colors">{job.title}</p>
                  <p className="text-[12px] text-slate-500 mt-1.5 flex items-center gap-1"><MapPin size={11} />{job.town}</p>
                </Link>
              ))}
            </div>
          </SectionShell>
        );
      }

      // No data for these in the home payload, so they never render a dead
      // shelf. Both remain reachable from the Explore grid below.
      case "community":
      case "freecycle":
        return null;

      case "explore":
        return (
          <SectionShell key={spec.id} title={spec.title} subtitle="Everything Ayrshire Hub can do">
            <ExploreGrid onNavigate={(href, label) => track("service_clicked", { screen: "home", metadata: { href, label } })} />
          </SectionShell>
        );

      default:
        return null;
    }
  }

  return (
    <div className="min-h-screen bg-background">

      {/* ── HERO ─────────────────────────────────────────────────
          Compact and contextual. The previous hero spent the whole first
          viewport on a greeting, a search box and seven equally-weighted
          tiles, so nothing you could actually buy appeared above the fold.
          Now: where you are, what you probably want, and one way to get it.
          The seven tiles moved to Explore at the foot of the page, which is
          where a directory belongs. */}
      <section className="relative isolate overflow-hidden bg-[#0c1e33] pt-6 pb-6 px-4">
        {/* The photo sits behind everything, with a scrim over it. The scrim is
            not decoration: white text on an unmasked photo of food is
            unreadable, and it has to hold at every crop from 320px to 1440px,
            so it runs dark from the left and again from the bottom rather than
            relying on any one part of the image staying quiet. */}
        {heroImage && (
          <>
            <img
              src={heroImage}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 -z-20 h-full w-full object-cover"
              // Above the fold and the largest thing on it, so it should not be
              // lazy — it is the LCP element.
              fetchPriority="high"
            />
            {/* Two light layers rather than one heavy one. A single dark wash
                strong enough to guarantee contrast hid the food altogether and
                left the hero looking like the flat navy panel it replaced.
                Instead: an even tint that lifts contrast everywhere, plus a
                stronger bottom band under the chips, which are small text on a
                busy part of the photo. The headline carries its own shadow. */}
            <div aria-hidden="true" className="absolute inset-0 -z-10 bg-[#0c1e33]/55" />
            <div
              aria-hidden="true"
              className="absolute inset-x-0 bottom-0 h-2/3 -z-10 bg-gradient-to-t from-[#0c1e33]/85 via-[#0c1e33]/45 to-transparent"
            />
          </>
        )}
        <div className="relative max-w-screen-xl mx-auto">
          <p className="text-white/75 text-[12px] font-medium mb-1.5 flex items-center gap-1 [text-shadow:0_1px_8px_rgba(6,15,28,0.8)]">
            <MapPin size={11} className="opacity-60" />
            {townLabel}
            {greeting && <span className="text-white/35"> · {greeting}</span>}
          </p>

          <h1 className="text-white text-[26px] sm:text-4xl font-extrabold leading-tight tracking-tight [text-shadow:0_2px_12px_rgba(6,15,28,0.75)]">
            {ctx?.orderHeadline ?? "Order local"}
          </h1>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4">
            <div className="grid grid-cols-2 gap-2 shrink-0">
              <Link href="/order" onClick={() => track("order_hero_clicked", { screen: "home", metadata: { dayPart: ctx?.dayPart } })}>
                <Button size="lg" className="bg-amber-500 hover:bg-amber-400 text-white font-bold w-full gap-2 shadow-lg shadow-amber-900/25 px-3 sm:px-4">
                  <ShoppingBag size={17} />
                  Takeaways
                </Button>
              </Link>
              <Link href="/shop-local?category=Groceries" onClick={() => track("grocery_hero_clicked", { screen: "home" })}>
                <Button size="lg" variant="outline" className="bg-white/95 hover:bg-white text-[#1e3a5f] border-white font-bold w-full gap-2 px-3 sm:px-4">
                  <Store size={17} />
                  Groceries
                </Button>
              </Link>
            </div>

            <form
              onSubmit={e => { e.preventDefault(); if (search.trim()) { track("homepage_search", { screen: "home", metadata: { q: search.trim().slice(0, 60) } }); navigate(`/search?q=${encodeURIComponent(search.trim())}`); } }}
              className="flex-1 min-w-0"
            >
              <div className="relative">
                <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  type="search"
                  aria-label="Search Ayrshire Hub"
                  placeholder={SEARCH_PLACEHOLDER[ctx?.dayPart ?? "dinner"]}
                  className="w-full h-11 pl-10 pr-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/35 text-[14px] font-medium focus:outline-none focus:bg-white/15 focus:border-white/25 transition-all"
                />
              </div>
            </form>
          </div>

          {/* What to eat, this hour — real categories that exist here. */}
          <div className="mt-4 -mx-4 px-4 min-h-12">
            {ctx ? <FoodCategoryRail
              categories={ctx.foodCategories}
              availableCategories={availableCategories}
              onPick={(c) => track("food_category_clicked", { screen: "home", metadata: { category: c, dayPart: ctx.dayPart } })}
            /> : <div className="h-12" aria-hidden="true" />}
          </div>
        </div>
      </section>

      {/* ── Basket recovery ────────────────────────────────────── */}
      {abandonedBasket && (
        <div className="px-4 max-w-screen-xl mx-auto mt-5">
          <Link href={`/order/${abandonedBasket.storeId}`}>
            <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3.5 flex items-center gap-3 hover:bg-amber-100 transition-colors cursor-pointer shadow-sm">
              <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <ShoppingBag size={16} className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-amber-900">Continue your order</p>
                <p className="text-xs text-amber-700/75 truncate">
                  {abandonedBasket.storeName ?? "Your basket"}
                  {abandonedBasket.estimatedSubtotalPence ? ` — £${(abandonedBasket.estimatedSubtotalPence / 100).toFixed(2)}` : ""}
                </p>
              </div>
              <RefreshCw size={14} className="text-amber-500 flex-shrink-0" />
            </div>
          </Link>
        </div>
      )}

      {/* ── Active order ───────────────────────────────────────── */}
      <ActiveOrderBanner />

      {/* ── Planned sections, in the order the context engine chose ── */}
      <div className="px-4 max-w-screen-xl mx-auto mt-7">
        {isLoading || foodLoading || retailLoading || dealsLoading ? (
          <div className="min-h-[650px]" role="status" aria-label="Loading local shops">
            <SectionShell title="Local shops" href="/order">
              <ScrollRow>{Array.from({ length: 5 }).map((_, i) => <SkeletonStoreCard key={i} />)}</ScrollRow>
            </SectionShell>
          </div>
        ) : <>
          <FeaturedDiscounts deals={homepageDeals} />
          {(ctx?.sections ?? DEFAULT_SECTIONS).map(renderSection)}
        </>}
      </div>

      {/* ── Multi-store feature callout ────────────────────────── */}
      {!foodLoading && (foodStores.length > 0 || retailStores.length > 0) && (
        <section className="mt-6 px-4 max-w-screen-xl mx-auto">
          {/* Links to browse, NOT /order/multi-pick. The companion picker
              requires ?anchorStoreId=&anchorSlug=&anchorName= — you can only
              add a SECOND store once you have a basket at a first one. */}
          <Link href="/order">
            <div className="rounded-2xl bg-primary/5 border border-primary/12 px-5 py-4 flex items-center gap-4 hover:border-primary/25 hover:bg-primary/8 transition-all cursor-pointer">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Sparkles size={17} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[13px] text-slate-800">One order, multiple shops</p>
                <p className="text-[12px] text-slate-500 mt-0.5">Order from different restaurants & shops in a single delivery — unique to Ayrshire Hub.</p>
              </div>
              <ArrowRight size={15} className="text-slate-400 flex-shrink-0" />
            </div>
          </Link>
        </section>
      )}

      {/* ── Nothing here ──────────────────────────────────────── */}
      {showNothingHere && (
        <section className="mt-12 px-4 max-w-screen-xl mx-auto text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-5"><MapPin size={24} className="text-slate-300" /></div>
          <h3 className="text-lg font-bold text-slate-700 mb-2">Nothing yet in {selectedTown}</h3>
          <p className="text-sm text-slate-400 mb-6 max-w-sm mx-auto">We're growing across Ayrshire. Switch to All Towns to explore everything available.</p>
          <Link href="/order"><Button variant="outline" size="sm">Browse all Ayrshire</Button></Link>
        </section>
      )}

      {/* ── Help & Sponsor ────────────────────────────────────── */}
      <section className="mt-8 px-4 max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SponsorBanner />
          <Link href="/help">
            <div className="flex items-center gap-4 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 hover:bg-red-100 transition-colors cursor-pointer h-full min-h-[72px]">
              <LifeBuoy size={20} className="text-red-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-slate-800">Need support?</p>
                <p className="text-xs text-slate-500 mt-0.5">Free helplines and local crisis resources</p>
              </div>
              <ChevronRight size={14} className="text-red-300 flex-shrink-0" />
            </div>
          </Link>
        </div>
      </section>

      {/* ── Business CTA ─────────────────────────────────────── */}
      <section className="mt-10" style={{ background: "linear-gradient(135deg, #0a1929 0%, #0f2744 50%, #0d2035 100%)" }}>
        <div className="max-w-screen-xl mx-auto px-4 py-14 flex flex-col md:flex-row items-center justify-between gap-10">
          <div>
            <p className="text-amber-400 text-[11px] font-bold uppercase tracking-widest mb-3">For local businesses</p>
            <h2 className="text-white text-2xl sm:text-3xl font-extrabold tracking-tight mb-3 leading-snug">
              Grow your business<br className="hidden sm:block" /> with Ayrshire Hub
            </h2>
            <p className="text-white/50 max-w-md text-sm leading-relaxed">
              Get found by thousands of Ayrshire residents. List your business, post deals, receive job leads and take food orders — all from one dashboard.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <Link href={user ? "/business" : "/signup"}>
              <Button size="lg" className="bg-amber-500 hover:bg-amber-400 text-white font-bold w-full sm:w-auto shadow-xl shadow-amber-900/30">
                {user ? "Go to Business Portal" : "List Your Business Free"}
              </Button>
            </Link>
            <Link href={hasStore ? "/business/order-store" : "/order"}>
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 bg-white/5 w-full sm:w-auto gap-2 font-semibold">
                <ShoppingBag size={15} /> {hasStore ? "Manage My Shops" : "Register Your Shop"}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Become a Driver ──────────────────────────────────── */}
      <section className="px-4 mt-8 max-w-screen-xl mx-auto">
        <Link href="/become-a-driver">
          <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-5 flex items-center gap-5 cursor-pointer hover:opacity-95 transition-opacity shadow-md">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0 border border-white/15">
              <Truck size={22} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-[15px] leading-tight">Drive with Ayrshire Hub</p>
              <p className="text-white/50 text-xs mt-0.5">Earn on your schedule. Local routes. Flexible hours.</p>
            </div>
            <ArrowRight size={18} className="text-white/40 flex-shrink-0" />
          </div>
        </Link>
      </section>

      {/* ── App Download ─────────────────────────────────────── */}
      <section className="px-4 mt-6 mb-12 max-w-screen-xl mx-auto">
        <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a8e 100%)" }}>
          <div className="px-6 sm:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <p className="text-amber-400 text-[10px] font-bold uppercase tracking-widest mb-2">Available now</p>
              <h2 className="text-xl font-extrabold text-white mb-1.5 tracking-tight">Get the Ayrshire Hub App</h2>
              <p className="text-white/50 text-sm leading-relaxed max-w-sm">Order food, track deliveries, get quote alerts and stay connected to your community.</p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <a href="https://apps.apple.com/gb/app/ayrshire-hub/id6766868864" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2.5 bg-black/60 border border-white/15 hover:bg-black/80 text-white rounded-xl px-4 py-2.5 transition-colors">
                <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
                <div className="flex flex-col leading-tight"><span className="text-[10px] text-white/50">Download on the</span><span className="text-sm font-bold">App Store</span></div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=uk.co.ayrshirehub.app" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2.5 bg-black/60 border border-white/15 hover:bg-black/80 text-white rounded-xl px-4 py-2.5 transition-colors">
                <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.18 23.76c.3.16.64.18.96.07l12.43-7.17-2.75-2.75-10.64 9.85z" fill="#EA4335" />
                  <path d="M22.47 10.26 19.1 8.31l-3.1 3.1 3.1 3.1 3.4-1.97c.97-.56.97-2.05-.03-2.28z" fill="#FBBC04" />
                  <path d="M2.22.36C1.9.67 1.71 1.17 1.71 1.83v20.34c0 .66.19 1.16.51 1.47l.08.07 11.4-11.4v-.27L2.3.29l-.08.07z" fill="#4285F4" />
                  <path d="M16.57 11.41 13.7 8.54 2.22.36c-.39-.22-.84-.24-1.23-.06l11.69 11.7 3.89-3.59z" fill="#34A853" />
                </svg>
                <div className="flex flex-col leading-tight"><span className="text-[10px] text-white/50">Get it on</span><span className="text-sm font-bold">Google Play</span></div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
